import { ApprovalRecipientNote } from '../components/leaves/ApprovalRecipientNote';
import { HrSeparateDates } from '../components/leaves/HrSeparateDates';
import { GroupDatesSelector } from '../components/leaves/GroupDatesSelector';
import { WaitingChip, ResendApprovalMenu, appliedAt } from '../components/leaves/PendingLeaveTools';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Gift, Download, AlertTriangle, Users, FileText, UserX, Eye, Clock, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import DuplicateScannerDialog from '../components/data-hygiene/DuplicateScannerDialog';import { 
  getEmployees, 
  getLeaves, 
  createLeave, 
  updateLeave, 
  getLeaveBalance, 
  deleteLeave, 
  addPHCredit, 
  getEmployeePHCredits, 
  getEmployeePHBalance, 
  deletePHCredit,
  setPHCreditExpiry,
  checkDocumentClash,
  getLeaveSplitPreview,
  createLeaveApprovalRequest,
  getLeaveApprovalSettings,
  resolveLeaveApprover,
  getBrokenLeaves
} from '../services/api';

// Import leaves components
import LeaveBalanceSection from '../components/leaves/LeaveBalanceSection';
import { BalanceAuditMenu } from '../components/leaves/BalanceAuditMenu';
import PHCreditsSection from '../components/leaves/PHCreditsSection';
import LeaveRecordsList from '../components/leaves/LeaveRecordsList';
import AdvancePaymentSection from '../components/leaves/AdvancePaymentSection';
import LeaveAuditDialog from '../components/leaves/LeaveAuditDialog';
import BrokenLeavesDialog from '../components/leaves/BrokenLeavesDialog';
import RecoveryLogDialog from '../components/leaves/RecoveryLogDialog';
import { LeaveSalaryPreviewDialog } from '../components/DocumentPreviewDialog';
import LeaveLedgerDialog from '../components/leaves/LeaveLedgerDialog';
import HistoricalLeaveCard from '../components/leaves/HistoricalLeaveCard';

const LEAVE_TYPES = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'emergency', label: 'Emergency Leave' },
  { value: 'unpaid', label: 'Unpaid Leave' },
  { value: 'mgmt_unpaid', label: 'Unpaid Leave by MGMT' },
  { value: 'maternity', label: 'Maternity Leave' },
  { value: 'paternity', label: 'Paternity Leave' },
  { value: 'public_holiday', label: 'Public Holiday (PH)' },
];

// Short labels + display order for the Leave Records type filter chips
const RECORD_TYPE_FILTERS = [
  { value: 'annual', label: 'Annual' },
  { value: 'sick', label: 'Sick' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'public_holiday', label: 'Public Holiday' },
  { value: 'maternity', label: 'Maternity' },
  { value: 'paternity', label: 'Paternity' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'mgmt_unpaid', label: 'MGMT Unpaid' },
  { value: 'partial_unpaid', label: 'Partial Unpaid' },
];

const Leaves = () => {
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [phDialogOpen, setPHDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [phCredits, setPHCredits] = useState([]);
  const [phBalance, setPHBalance] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);
  const [formExtraPaid, setFormExtraPaid] = useState(0);
  const [managers, setManagers] = useState([]);  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dlMonth, setDlMonth] = useState('');
  const [dlOpen, setDlOpen] = useState(false);
  const [dlDept, setDlDept] = useState('all');
  const [dlTypes, setDlTypes] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [portalTransparency, setPortalTransparency] = useState(false);
  const [overdrawMap, setOverdrawMap] = useState({});
  const [documentWarnings, setDocumentWarnings] = useState([]);
  const [phFormData, setPHFormData] = useState({
    employee_id: '',
    earned_date: '',
    days: 1,
    reason: ''
  });
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    reason: '',
    flight_ticket_provided: false
  });
  // Multi-range mode for "employee took leave on multiple non-consecutive dates"
  const [multiMode, setMultiMode] = useState(false);
  const [ranges, setRanges] = useState([{ start_date: '', end_date: '' }]);
  // Live paid/unpaid (LOP) split preview for the create/edit dialog
  const [splitPreview, setSplitPreview] = useState(null);
  const [splitLoading, setSplitLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [deptReportOpen, setDeptReportOpen] = useState(false);
  const [selectedDeptReport, setSelectedDeptReport] = useState('');
  const [ltbOpen, setLtbOpen] = useState(false);
  const [ltbYear, setLtbYear] = useState(new Date().getFullYear().toString());
  const [ltbData, setLtbData] = useState(null);
  const [ltbLoading, setLtbLoading] = useState(false);
  const [ltbDept, setLtbDept] = useState('all');
  const [ltbFrom, setLtbFrom] = useState('1');
  const [ltbTo, setLtbTo] = useState('12');
  const [deptReportMonth, setDeptReportMonth] = useState('');
  const [deptReportYear, setDeptReportYear] = useState(new Date().getFullYear().toString());
  const [deptReportIncludeInactive, setDeptReportIncludeInactive] = useState(false);
  const [deptReportLeaveType, setDeptReportLeaveType] = useState('');
  const [showOldEmployees, setShowOldEmployees] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [hrRejectDates, setHrRejectDates] = useState({});
  
  // Leave Salary Download state
  const [leaveSalaryDialogOpen, setLeaveSalaryDialogOpen] = useState(false);
  const [leaveSalaryPreviewOpen, setLeaveSalaryPreviewOpen] = useState(false);
  const [leaveSalaryMonth, setLeaveSalaryMonth] = useState(new Date().getMonth() + 1);
  const [leaveSalaryYear, setLeaveSalaryYear] = useState(new Date().getFullYear());
  const [leaveStartDate, setLeaveStartDate] = useState('');
  const [leaveEndDate, setLeaveEndDate] = useState('');
  const [leaveSalaryType, setLeaveSalaryType] = useState('annual');
  const [showAudit, setShowAudit] = useState(false);
  const [showCleanup, setShowCleanup] = useState(false);
  const [showRecoveryLog, setShowRecoveryLog] = useState(false);
  const [brokenCount, setBrokenCount] = useState(0);
  const [showDupeScanner, setShowDupeScanner] = useState(false);
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Email-based approval workflow state
  const [approverEmail, setApproverEmail] = useState('');
  const [sendingApproval, setSendingApproval] = useState(false);
  const [approvalsRefresh, setApprovalsRefresh] = useState(0);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer';

  useEffect(() => {
    loadData();
    loadBrokenCount();
    // Load the portal-visibility toggle for leave transparency
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leaves/transparency-settings`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setPortalTransparency(!!r.data.portal_enabled)).catch(() => {});
  }, []);

  // Fetch overdraw previews for pending annual/partial leaves (approval warning)
  useEffect(() => {
    const annualPending = pendingLeaves.filter(l => ['annual', 'partial_unpaid'].includes(l.leave_type));
    if (annualPending.length === 0) { setOverdrawMap({}); return; }
    const tok = localStorage.getItem('token');
    Promise.all(annualPending.map(l =>
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leaves/overdraw-preview/${l.id}`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => [l.id, r.data]).catch(() => [l.id, null])
    )).then(pairs => {
      const m = {};
      pairs.forEach(([id, d]) => { if (d && d.would_overdraw) m[id] = d; });
      setOverdrawMap(m);
    });
  }, [pendingLeaves]);

  const togglePortalTransparency = async () => {
    const next = !portalTransparency;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/leaves/transparency-settings`, { portal_enabled: next }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setPortalTransparency(next);
      toast({ title: next ? 'Enabled on Employee Portal' : 'Disabled on Employee Portal', description: 'Leave breakdown & ledger visibility updated.' });
    } catch { toast({ title: 'Could not update setting', variant: 'destructive' }); }
  };

  useEffect(() => {
    // Prefill approver email with the configured default
    getLeaveApprovalSettings()
      .then((res) => {
        const d = res.data?.default_approver_email;
        if (d) setApproverEmail(d);
      })
      .catch(() => {});
  }, [approvalsRefresh]);

  useEffect(() => {
    // Reload employees when showOldEmployees changes
    loadEmployees();
  }, [showOldEmployees]);

  // Live paid/unpaid (LOP) split preview while HR fills the leave dialog
  useEffect(() => {
    const { employee_id, start_date, end_date, leave_type } = formData;
    if (!dialogOpen || multiMode || !employee_id || !start_date || !end_date) {
      setSplitPreview(null);
      return;
    }
    if (end_date < start_date) {
      setSplitPreview(null);
      return;
    }
    let cancelled = false;
    setSplitLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await getLeaveSplitPreview(employee_id, start_date, end_date, leave_type, editingLeave?.id || null);
        if (!cancelled) setSplitPreview(res.data);
      } catch {
        if (!cancelled) setSplitPreview(null);
      } finally {
        if (!cancelled) setSplitLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [formData, dialogOpen, multiMode, editingLeave]);

  const loadEmployees = async () => {
    try {
      const empsRes = await getEmployees(!showOldEmployees);
      setAllEmployees(empsRes.data);
      
      // For the main list, separate active and inactive
      if (showOldEmployees) {
        // Show all employees, with inactive ones marked
        setEmployees(empsRes.data);
      } else {
        // Show only active employees
        setEmployees(empsRes.data.filter(e => e.employment_status === 'Active' || !e.employment_status));
      }
      
      // Extract unique departments from all employees
      const uniqueDepts = [...new Set(empsRes.data.map(e => e.department).filter(Boolean))].sort();
      setDepartments(uniqueDepts);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadData = async () => {
    const [empsRes, leavesRes, allEmpsRes] = await Promise.allSettled([
      getEmployees(!showOldEmployees),
      getLeaves(),
      getEmployees(false)  // Get all employees (including inactive) for department list
    ]);

    if (empsRes.status === 'fulfilled') {
      setEmployees(empsRes.value.data);
      setAllEmployees(empsRes.value.data);
    }

    if (leavesRes.status === 'fulfilled') {
      const allLeaves = leavesRes.value.data;
      setLeaves(allLeaves);
      setPendingLeaves(allLeaves.filter(l => l.status === 'pending' && !l.group_follower).map(l => (l.group_id ? {
        ...l, pending_dates: allLeaves.filter(x => x.group_id === l.group_id && x.status === 'pending').map(x => String(x.start_date).slice(0, 10)).sort(),
      } : l)).sort((a, b) => String(appliedAt(a) || '').localeCompare(String(appliedAt(b) || ''))));
    }

    if (allEmpsRes.status === 'fulfilled') {
      const uniqueDepts = [...new Set(allEmpsRes.value.data.map(e => e.department).filter(Boolean))].sort();
      setDepartments(uniqueDepts);
    }

    if (empsRes.status === 'rejected' || leavesRes.status === 'rejected' || allEmpsRes.status === 'rejected') {
      console.error('loadData partial failure', { empsRes, leavesRes, allEmpsRes });
      toast({ title: 'Error', description: 'Some data failed to load', variant: 'destructive' });
    }
  };

  const loadBrokenCount = async () => {
    try {
      const res = await getBrokenLeaves();
      setBrokenCount(res.data.count || 0);
    } catch (e) {
      // non-critical; leave count at 0
    }
  };

  const loadBalance = async (employeeId) => {
    try {
      const response = await getLeaveBalance(employeeId);
      setLeaveBalance(response.data);
    } catch (error) {
      console.error('Failed to load balance:', error);
    }
  };

  // Check for document expiry clash when dates change
  const checkForDocumentClash = async (employeeId, startDate, endDate) => {
    if (!employeeId || !startDate || !endDate) {
      setDocumentWarnings([]);
      return;
    }
    try {
      const response = await checkDocumentClash(employeeId, startDate, endDate);
      if (response.data.has_warnings) {
        setDocumentWarnings(response.data.warnings);
      } else {
        setDocumentWarnings([]);
      }
    } catch (error) {
      console.error('Failed to check document clash:', error);
      setDocumentWarnings([]);
    }
  };

  // Handle form field changes and trigger clash check
  const handleFormChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Check for document clash when employee or dates change
    if (field === 'employee_id' || field === 'start_date' || field === 'end_date') {
      checkForDocumentClash(
        field === 'employee_id' ? value : newFormData.employee_id,
        field === 'start_date' ? value : newFormData.start_date,
        field === 'end_date' ? value : newFormData.end_date
      );
    }

    // Auto-resolve the approver email (per-department → default) on employee change
    if (field === 'employee_id') {
      const emp = (allEmployees && allEmployees.length ? allEmployees : employees).find(e => e.id === value);
      const dept = emp?.department || '';
      resolveLeaveApprover(dept)
        .then((res) => {
          const resolved = res.data?.approver_email;
          if (resolved) setApproverEmail(resolved);
        })
        .catch(() => {});
      // HR reminder: does this employee have extra days paid in advance?
      setFormExtraPaid(0);
      if (value) {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leaves/extra-paid/${value}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
          .then((res) => setFormExtraPaid(res.data?.extra_paid_days || 0))
          .catch(() => {});
      }
    }
  };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leaves/managers-list`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((res) => setManagers(res.data?.managers || []))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingLeave) {
        await updateLeave(editingLeave.id, formData);
        toast({ title: 'Success', description: 'Leave updated successfully' });
      } else if (multiMode) {
        // Batch create — multiple non-consecutive ranges for the same employee
        const cleanRanges = ranges.filter(r => r.start_date && r.end_date);
        if (cleanRanges.length === 0) {
          toast({ title: 'No ranges', description: 'Add at least one date range', variant: 'destructive' });
          return;
        }
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/leaves/batch`,
          {
            employee_id: formData.employee_id,
            leave_type: formData.leave_type,
            reason: formData.reason || '',
            flight_ticket_provided: formData.flight_ticket_provided,
            is_paid: true,
            status: 'taken',
            approved_by_manager: formData.approved_by_manager || null,
            approved_by_manager_id: formData.approved_by_manager_id || null,
            ranges: cleanRanges,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { created_count, error_count, errors } = res.data;
        if (error_count === 0) {
          toast({ title: 'Success', description: `${created_count} leave entries recorded` });
        } else {
          toast({
            title: `${created_count} created, ${error_count} failed`,
            description: errors?.map(e => `Range ${e.index + 1}: ${e.error}`).join(' • ') || '',
            variant: 'destructive',
          });
        }
      } else {
        // Handle both the duplicate (409 leave_duplicate) and the
        // over-balance (409 leave_exceeds_balance) confirmation prompts.
        const submitWithConfirms = async (confirmDup, confirmLop) => {
          try {
            await createLeave(formData, confirmDup, confirmLop);
            return true;
          } catch (err) {
            const detail = err?.response?.data?.detail;
            const status = err?.response?.status;
            if (status === 409 && detail?.code === 'leave_duplicate' && !confirmDup) {
              const list = (detail.conflicts || []).map(c =>
                `• ${c.leave_type || 'leave'} ${c.start_date} → ${c.end_date} (${c.days || '?'}d, ${c.status || ''})`
              ).join('\n');
              if (!window.confirm(
                `⚠ Duplicate leave warning\n\n${detail.message}\n\nConflicting leaves:\n${list}\n\nCreate it anyway?`
              )) {
                return false;
              }
              return submitWithConfirms(true, confirmLop);
            }
            if (status === 409 && detail?.code === 'leave_exceeds_balance' && !confirmLop) {
              if (!window.confirm(
                `⚠ Leave exceeds available balance\n\n${detail.message}\n\n` +
                `The ${detail.unpaid_days} excess day(s) will be recorded as UNPAID (LOP) ` +
                `and deducted from payroll.\n\nRecord it anyway?`
              )) {
                return false;
              }
              return submitWithConfirms(confirmDup, true);
            }
            throw err;
          }
        };
        const ok = await submitWithConfirms(false, false);
        if (!ok) return;
        toast({ title: 'Success', description: 'Leave recorded successfully' });
      }
      loadData();
      setDialogOpen(false);
      setEditingLeave(null);
      setDocumentWarnings([]);
      setMultiMode(false);
      setRanges([{ start_date: '', end_date: '' }]);
      setFormData({
        employee_id: '',
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
        flight_ticket_provided: false
      });
    } catch (error) {
      toast({ title: 'Error', description: error?.response?.data?.detail || 'Failed to save leave', variant: 'destructive' });
    }
  };

  const handleSendForApproval = async () => {
    if (!formData.employee_id || !formData.start_date || !formData.end_date) {
      toast({ title: 'Missing details', description: 'Select an employee and both dates first', variant: 'destructive' });
      return;
    }
    const email = (approverEmail || '').trim();
    if (!email || !email.includes('@')) {
      toast({ title: 'Approver email required', description: 'Enter the manager email to send the approval request', variant: 'destructive' });
      return;
    }
    setSendingApproval(true);
    try {
      const res = await createLeaveApprovalRequest({
        employee_id: formData.employee_id,
        leave_type: formData.leave_type,
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason || '',
        flight_ticket_provided: !!formData.flight_ticket_provided,
        is_paid: true,
        approver_email: email,
      });
      if (res.data.email_sent) {
        toast({ title: 'Sent for approval', description: `Approval email sent to ${email}` });
      } else {
        toast({ title: 'Request created', description: `Email could not be sent (${res.data.email_error || 'check email config'}). Link is available in the approvals list.`, variant: 'destructive' });
      }
      setApprovalsRefresh((n) => n + 1);
      setDialogOpen(false);
      setDocumentWarnings([]);
      setFormData({ employee_id: '', leave_type: 'annual', start_date: '', end_date: '', reason: '', flight_ticket_provided: false });
    } catch (error) {
      toast({ title: 'Error', description: error?.response?.data?.detail || 'Failed to send for approval', variant: 'destructive' });
    } finally {
      setSendingApproval(false);
    }
  };

  const handleEdit = (leave) => {
    setEditingLeave(leave);
    setFormData({
      employee_id: leave.employee_id,
      leave_type: leave.leave_type,
      start_date: leave.start_date,
      end_date: leave.end_date,
      reason: leave.reason || '',
      flight_ticket_provided: leave.flight_ticket_provided || false,
      approved_by_manager: leave.approved_by_manager || '',
      approved_by_manager_id: leave.approved_by_manager_id || '',
    });
    setDocumentWarnings([]);
    checkForDocumentClash(leave.employee_id, leave.start_date, leave.end_date);
    setDialogOpen(true);
  };

  const handleDeleteLeave = async (leaveId) => {
    // Find the leave to get its details for the confirmation dialog
    const leave = leaves.find(l => l.id === leaveId);
    setLeaveToDelete(leave);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteLeave = async () => {
    if (!leaveToDelete) return;
    setDeleting(true);
    
    try {
      await deleteLeave(leaveToDelete.id);
      toast({ title: 'Success', description: 'Leave deleted successfully' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete leave', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setLeaveToDelete(null);
    }
  };

  const handleDialogClose = (open) => {
    if (!open) {
      setDialogOpen(false);
      setEditingLeave(null);
      setDocumentWarnings([]);
      setMultiMode(false);
      setRanges([{ start_date: '', end_date: '' }]);
      setFormData({
        employee_id: '',
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
        flight_ticket_provided: false
      });
    } else {
      setDialogOpen(true);
    }
  };

  const handleDownloadReport = async (format) => {
    try {
      toast({ title: 'Preparing Download', description: `Generating leave balance report (${format.toUpperCase()})...` });
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/downloads/leave-balance-report?format=${format}`);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Leave_Balance_Report_${new Date().getFullYear()}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 5000);
      
      toast({ title: 'Download Complete', description: 'Leave balance report downloaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download report', variant: 'destructive' });
    }
  };

  const handleDownloadAllApplications = async (types = [], monthArg = undefined, dept = '') => {
    try {
      const yr = (selectedYear && selectedYear !== 'all') ? selectedYear : new Date().getFullYear();
      const mo = monthArg !== undefined ? monthArg : (selectedMonth || '');
      const monthParam = mo ? `&month=${mo}` : '';
      const cleanTypes = (types || []).filter(t => t && t !== 'all');
      const typeParam = cleanTypes.length ? `&leave_type=${cleanTypes.join(',')}` : '';
      const deptParam = (dept && dept !== 'all') ? `&department=${encodeURIComponent(dept)}` : '';
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const typeLabels = { annual: 'Annual', sick: 'Sick', public_holiday: 'Public Holiday', unpaid: 'Unpaid', mgmt_unpaid: 'MGMT Unpaid', partial_unpaid: 'Partial-paid' };
      const typeLabel = cleanTypes.length ? cleanTypes.map(t => typeLabels[t] || t).join(' + ') : 'All types';
      const label = `${typeLabel} · ${mo ? `${monthNames[mo - 1]} ` : ''}${yr}${dept && dept !== 'all' ? ` · ${dept}` : ''}`;
      toast({ title: 'Preparing Download', description: `Merging leave applications (${label})...` });
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/leaves/applications/bulk-pdf?year=${yr}${monthParam}${typeParam}${deptParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 404) {
        toast({ title: 'No applications', description: `No ${label} leave applications found.`, variant: 'destructive' });
        return;
      }
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `LeaveApplications_${label.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 5000);
      toast({ title: 'Download Complete', description: `Leave applications (${label}) downloaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download applications', variant: 'destructive' });
    }
  };


  const ltbQuery = () => `year=${ltbYear}&department=${encodeURIComponent(ltbDept)}&month_from=${ltbFrom}&month_to=${ltbTo}`;

  const fetchLtb = async () => {
    setLtbLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/downloads/leave-type-breakdown?${ltbQuery()}&format=json`);
      if (!res.ok) throw new Error('failed');
      setLtbData(await res.json());
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load leave-type breakdown', variant: 'destructive' });
    } finally { setLtbLoading(false); }
  };

  useEffect(() => { if (ltbOpen) fetchLtb(); }, [ltbOpen, ltbYear, ltbDept, ltbFrom, ltbTo]);

  const downloadLtb = async (format, employeeId = null) => {
    try {
      toast({ title: 'Preparing Download', description: `Generating leave report (${format.toUpperCase()})...` });
      const empParam = employeeId ? `&employee_id=${encodeURIComponent(employeeId)}` : '';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/downloads/leave-type-breakdown?${ltbQuery()}&format=${format}${empParam}`);
      if (!res.ok) throw new Error('failed');
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = m ? m[1] : `Leave_Report_${ltbYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 4000);
      toast({ title: 'Download Complete' });
    } catch (e) {
      toast({ title: 'Error', description: 'Download failed', variant: 'destructive' });
    }
  };

  const handleDepartmentReport = async (format) => {
    if (!selectedDeptReport) {
      toast({ title: 'Error', description: 'Please select a department', variant: 'destructive' });
      return;
    }
    try {
      const year = parseInt(deptReportYear);
      const monthVal = deptReportMonth && deptReportMonth !== 'all' ? deptReportMonth : '';
      const leaveTypeVal = deptReportLeaveType && deptReportLeaveType !== 'all' ? deptReportLeaveType : '';
      const monthParam = monthVal ? `&month=${monthVal}` : '';
      const inactiveParam = deptReportIncludeInactive ? '&include_inactive=true' : '';
      const leaveTypeParam = leaveTypeVal ? `&leave_type=${leaveTypeVal}` : '';
      const monthLabel = monthVal ? `_${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(monthVal)-1]}` : '';
      const typeLabel = leaveTypeVal ? `_${leaveTypeVal}` : '';
      
      toast({ title: 'Preparing Download', description: `Generating ${selectedDeptReport} leave report (${format.toUpperCase()})...` });
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/downloads/leave-report/department/${encodeURIComponent(selectedDeptReport)}?year=${year}&format=${format}${monthParam}${inactiveParam}${leaveTypeParam}`);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Leave_Report_${selectedDeptReport}${typeLabel}${monthLabel}_${year}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 5000);
      
      toast({ title: 'Download Complete', description: `${selectedDeptReport} leave report downloaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download department report', variant: 'destructive' });
    }
  };

  const handleLeaveAction = async (leaveId, action) => {
    const lv = pendingLeaves.find(l => l.id === leaveId);
    if (lv?.group_id) {
      try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leaves/groups/${lv.group_id}/decide`,
          { action, reject_dates: action === 'approve' ? (hrRejectDates[leaveId] || []) : [] },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        toast({ title: 'Success', description: `${res.data.approved.length} date(s) approved, ${res.data.rejected.length} rejected` });
        setHrRejectDates((m) => ({ ...m, [leaveId]: [] }));
        loadData();
      } catch (error) {
        toast({ title: 'Error', description: error.response?.data?.detail || `Failed to ${action} leave`, variant: 'destructive' });
      }
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employee-portal/hr-leave-action/${leaveId}?action=${action}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Action failed');
      toast({ title: 'Success', description: `Leave ${action}d successfully` });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: `Failed to ${action} leave`, variant: 'destructive' });
    }
  };

  // Download Leave Salary for selected employee
  const handleDownloadLeaveSalary = async (format) => {
    if (!selectedEmployee || selectedEmployee === 'all') {
      toast({ title: 'Error', description: 'Please select an employee first', variant: 'destructive' });
      return;
    }
    
    try {
      // Validate dates are provided
      if (!leaveStartDate || !leaveEndDate) {
        toast({ title: 'Error', description: 'Please select both start and end dates', variant: 'destructive' });
        return;
      }
      
      // Derive year/month from start date
      const startDt = new Date(leaveStartDate);
      const derivedYear = startDt.getFullYear();
      const derivedMonth = startDt.getMonth() + 1;
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                         'July', 'August', 'September', 'October', 'November', 'December'];
      const monthName = monthNames[derivedMonth - 1];
      
      toast({ title: 'Preparing Download', description: `Generating Leave Salary...` });
      
      // Get auth token for "Prepared by" field
      const token = localStorage.getItem('token');
      
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/downloads/leave-salary/${selectedEmployee}?year=${derivedYear}&month=${derivedMonth}&format=${format}&leave_type=${leaveSalaryType}&leave_start_date=${leaveStartDate}&leave_end_date=${leaveEndDate}`;
      
      // Add authorization parameter (for "Prepared by" logic)
      if (token) {
        url += `&authorization=${encodeURIComponent('Bearer ' + token)}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      const emp = employees.find(e => e.id === selectedEmployee);
      const empCode = emp?.employee_code || selectedEmployee;
      link.download = `LeaveSalary_${empCode}_${monthName}_${derivedYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 5000);
      
      toast({ title: 'Download Complete', description: 'Leave Salary downloaded successfully' });
      setLeaveSalaryDialogOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download Leave Salary', variant: 'destructive' });
    }
  };

  // Direct download from a leave record row
  const handleDownloadLeaveRecordSalary = async (leave) => {
    try {
      const startDt = new Date(leave.start_date);
      const derivedYear = startDt.getFullYear();
      const derivedMonth = startDt.getMonth() + 1;
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                         'July', 'August', 'September', 'October', 'November', 'December'];
      const monthName = monthNames[derivedMonth - 1];
      
      toast({ title: 'Preparing Download', description: `Generating Leave Salary for ${leave.employee_name}...` });
      
      const token = localStorage.getItem('token');
      
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/downloads/leave-salary/${leave.employee_id}?year=${derivedYear}&month=${derivedMonth}&format=pdf&leave_type=${leave.leave_type}&leave_start_date=${leave.start_date}&leave_end_date=${leave.end_date}`;
      
      if (token) {
        url += `&authorization=${encodeURIComponent('Bearer ' + token)}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      const emp = employees.find(e => e.id === leave.employee_id);
      const empCode = emp?.employee_code || leave.employee_id;
      link.download = `LeaveSalary_${empCode}_${monthName}_${derivedYear}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 5000);
      
      toast({ title: 'Download Complete', description: `Leave Salary for ${leave.employee_name} downloaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download Leave Salary', variant: 'destructive' });
    }
  };

  const handleEmployeeSelect = async (employeeId) => {
    setSelectedEmployee(employeeId);
    if (employeeId && employeeId !== 'all') {
      loadBalance(employeeId);
      try {
        const [creditsRes, balanceRes] = await Promise.all([
          getEmployeePHCredits(employeeId),
          getEmployeePHBalance(employeeId)
        ]);
        setPHCredits(creditsRes.data);
        setPHBalance(balanceRes.data);
      } catch (error) {
        console.error('Failed to load PH data:', error);
      }
    } else {
      setLeaveBalance(null);
      setPHCredits([]);
      setPHBalance(null);
    }
  };

  const handleAddPHCredit = async (e) => {
    e.preventDefault();
    try {
      await addPHCredit(phFormData);
      toast({ title: 'Success', description: 'Public Holiday credit added' });
      setPHDialogOpen(false);
      setPHFormData({ employee_id: '', earned_date: '', days: 1, reason: '' });
      if (selectedEmployee && selectedEmployee !== 'all') {
        const [creditsRes, balanceRes] = await Promise.all([
          getEmployeePHCredits(selectedEmployee),
          getEmployeePHBalance(selectedEmployee)
        ]);
        setPHCredits(creditsRes.data);
        setPHBalance(balanceRes.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add PH credit', variant: 'destructive' });
    }
  };

  const handleDeletePHCredit = async (phId) => {
    if (!confirm('Are you sure you want to delete this PH credit?')) return;
    try {
      await deletePHCredit(phId);
      toast({ title: 'Success', description: 'PH credit deleted' });
      if (selectedEmployee && selectedEmployee !== 'all') {
        const [creditsRes, balanceRes] = await Promise.all([
          getEmployeePHCredits(selectedEmployee),
          getEmployeePHBalance(selectedEmployee)
        ]);
        setPHCredits(creditsRes.data);
        setPHBalance(balanceRes.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete PH credit', variant: 'destructive' });
    }
  };

  const handleSetPHExpiry = async (phId, currentExpiry) => {
    const input = window.prompt(
      'Set PH credit expiry date (YYYY-MM-DD).\nLeave blank and press OK to make it never expire.',
      currentExpiry || ''
    );
    if (input === null) return; // cancelled
    const val = input.trim();
    if (val && !/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      toast({ title: 'Invalid date', description: 'Use YYYY-MM-DD format', variant: 'destructive' });
      return;
    }
    try {
      await setPHCreditExpiry(phId, val || null);
      toast({ title: 'Success', description: val ? `Expiry set to ${val}` : 'Credit set to never expire' });
      if (selectedEmployee && selectedEmployee !== 'all') {
        const [creditsRes, balanceRes] = await Promise.all([
          getEmployeePHCredits(selectedEmployee),
          getEmployeePHBalance(selectedEmployee)
        ]);
        setPHCredits(creditsRes.data);
        setPHBalance(balanceRes.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update expiry', variant: 'destructive' });
    }
  };

  const dateScopedLeaves = (() => {
    let result = (selectedEmployee && selectedEmployee !== 'all')
      ? leaves.filter(l => l.employee_id === selectedEmployee)
      : leaves;
    if (selectedYear !== null && selectedYear !== 'all') {
      result = result.filter(l => {
        const sd = l.start_date || '';
        const ed = l.end_date || sd;
        const sy = parseInt(sd.split('-')[0], 10);
        const ey = parseInt(ed.split('-')[0], 10);
        // include if any portion overlaps selected year
        return sy === selectedYear || ey === selectedYear || (sy < selectedYear && ey > selectedYear);
      });
    }
    if (selectedMonth !== null) {
      result = result.filter(l => {
        const sd = l.start_date || '';
        const ed = l.end_date || sd;
        const sy = parseInt(sd.split('-')[0], 10);
        const sm = parseInt(sd.split('-')[1], 10);
        const ey = parseInt(ed.split('-')[0], 10);
        const em = parseInt(ed.split('-')[1], 10);
        const startKey = sy * 12 + sm;
        const endKey = ey * 12 + em;
        const targetYear = (selectedYear !== null && selectedYear !== 'all') ? selectedYear : sy;
        const targetKey = targetYear * 12 + selectedMonth;
        return targetKey >= startKey && targetKey <= endKey;
      });
    }
    return result;
  })();

  // Per-type counts (within the current year/month/employee scope) for the filter chips
  const typeCounts = dateScopedLeaves.reduce((acc, l) => {
    const t = l.leave_type || 'other';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const filteredLeaves = selectedLeaveType === 'all'
    ? dateScopedLeaves
    : dateScopedLeaves.filter(l => (l.leave_type || 'other') === selectedLeaveType);

  return (
    <div className="space-y-6" data-testid="leaves-page">
      {/* Leave Type Breakdown dialog */}
      <Dialog open={ltbOpen} onOpenChange={setLtbOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="leave-type-breakdown-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-teal-600" /> Leave Type Breakdown</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Label className="text-xs">Year</Label>
                <Select value={ltbYear} onValueChange={setLtbYear}>
                  <SelectTrigger className="w-24 h-8" data-testid="ltb-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 3 + i).map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-xs">Dept</Label>
                <Select value={ltbDept} onValueChange={setLtbDept}>
                  <SelectTrigger className="w-40 h-8" data-testid="ltb-dept"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-xs">From</Label>
                <Select value={ltbFrom} onValueChange={setLtbFrom}>
                  <SelectTrigger className="w-24 h-8" data-testid="ltb-from"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                      <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-xs">To</Label>
                <Select value={ltbTo} onValueChange={setLtbTo}>
                  <SelectTrigger className="w-24 h-8" data-testid="ltb-to"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                      <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadLtb('pdf')} data-testid="ltb-pdf-btn"><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
              <Button size="sm" variant="outline" onClick={() => downloadLtb('excel')} data-testid="ltb-excel-btn"><Download className="h-3.5 w-3.5 mr-1" /> Excel</Button>
            </div>
          </div>
          <div className="overflow-auto border rounded-lg mt-2">
            {ltbLoading ? (
              <div className="py-16 text-center text-sm text-gray-500">Loading…</div>
            ) : !ltbData ? (
              <div className="py-16 text-center text-sm text-gray-500">No data</div>
            ) : (
              <table className="w-full text-sm" data-testid="ltb-table">
                <thead className="sticky top-0 bg-teal-600 text-white">
                  <tr>
                    <th className="text-left p-2">Code</th>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Dept</th>
                    {(ltbData.columns || []).map(c => <th key={c.key} className="text-right p-2">{c.label}</th>)}
                    <th className="text-right p-2">Total</th>
                    <th className="text-center p-2">Report</th>
                  </tr>
                </thead>
                <tbody>
                  {(ltbData.rows || []).map((r, i) => (
                    <tr key={r.employee_code || i} className={i % 2 ? 'bg-gray-50' : ''} data-testid={`ltb-row-${i}`}>
                      <td className="p-2 font-mono text-xs">{r.employee_code}</td>
                      <td className="p-2">{r.employee_name}</td>
                      <td className="p-2 text-xs text-gray-500">{r.department}</td>
                      {(ltbData.columns || []).map(c => <td key={c.key} className="p-2 text-right font-mono">{r[c.key] || 0}</td>)}
                      <td className="p-2 text-right font-mono font-semibold">{r.total || 0}</td>
                      <td className="p-2 text-center whitespace-nowrap">
                        <button title="Individual PDF" onClick={() => downloadLtb('pdf', r.employee_id)} data-testid={`ltb-ind-pdf-${r.employee_code}`} className="text-rose-600 hover:bg-rose-50 rounded px-1.5 py-0.5 text-[11px] font-medium">PDF</button>
                        <button title="Individual Excel" onClick={() => downloadLtb('excel', r.employee_id)} data-testid={`ltb-ind-xls-${r.employee_code}`} className="text-emerald-700 hover:bg-emerald-50 rounded px-1.5 py-0.5 text-[11px] font-medium ml-1">XLS</button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-amber-100 font-bold sticky bottom-0">
                    <td className="p-2" colSpan={3}>TOTALS</td>
                    {(ltbData.columns || []).map(c => <td key={c.key} className="p-2 text-right font-mono">{ltbData.totals?.[c.key] || 0}</td>)}
                    <td className="p-2 text-right font-mono">{ltbData.totals?.total || 0}</td>
                    <td className="p-2"></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          <p className="text-[11px] text-gray-400">Filter by department and month range; use the per-row PDF/XLS buttons for an individual employee report (includes a detailed leave list). "Annual" includes the paid portion of partial-unpaid leaves; "Unpaid" includes management-unpaid and the unpaid portion of partial leaves.</p>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold">Leave Management</h1>
          <p className="text-gray-600 mt-1">Track employee leaves and balances</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Download Report Buttons */}
          <BalanceAuditMenu />
          <Button variant="outline" onClick={() => handleDownloadReport('pdf')} data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-2" /> PDF Report
          </Button>
          <Button variant="outline" onClick={() => handleDownloadReport('excel')} data-testid="download-excel-btn">
            <Download className="h-4 w-4 mr-2" /> Excel Report
          </Button>
          <Popover open={dlOpen} onOpenChange={setDlOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="download-all-applications-btn" className="border-violet-200 text-violet-700 hover:bg-violet-50">
                <FileText className="h-4 w-4 mr-2" /> Download all applications
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="center">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Download applications</p>
                  <p className="text-[11px] text-gray-500">Filter by month, department & leave type</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Month</label>
                    <select
                      value={dlMonth}
                      onChange={(e) => setDlMonth(e.target.value)}
                      data-testid="dl-month-select"
                      className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm bg-white"
                    >
                      <option value="">Whole year</option>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Department</label>
                    <select
                      value={dlDept}
                      onChange={(e) => setDlDept(e.target.value)}
                      data-testid="dl-dept-select"
                      className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm bg-white"
                    >
                      <option value="all">All departments</option>
                      {departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5">Leave type <span className="text-gray-400">(tick one or more; none = all)</span></p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {[
                      { v: 'annual', l: 'Annual' },
                      { v: 'sick', l: 'Sick' },
                      { v: 'public_holiday', l: 'Public Holiday' },
                      { v: 'unpaid', l: 'Unpaid' },
                      { v: 'mgmt_unpaid', l: 'MGMT Unpaid' },
                      { v: 'partial_unpaid', l: 'Partial-paid' },
                    ].map(t => (
                      <label key={t.v} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`dl-type-${t.v}`}>
                        <input
                          type="checkbox"
                          checked={dlTypes.includes(t.v)}
                          onChange={(e) => setDlTypes(prev => e.target.checked ? [...prev, t.v] : prev.filter(x => x !== t.v))}
                          data-testid={`dl-type-check-${t.v}`}
                        />
                        {t.l}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t">
                  <button className="text-[11px] text-gray-400 hover:text-gray-600" onClick={() => setDlTypes([])} data-testid="dl-clear-types">Clear types</button>
                  <Button
                    size="sm"
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="dl-download-btn"
                    onClick={() => { setDlOpen(false); handleDownloadAllApplications(dlTypes, dlMonth ? Number(dlMonth) : '', dlDept); }}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Leave Type Breakdown */}
          <Button variant="outline" onClick={() => setLtbOpen(true)} data-testid="leave-type-breakdown-btn" className="border-teal-200 text-teal-700 hover:bg-teal-50">
            <FileText className="h-4 w-4 mr-2" /> Leave Types
          </Button>
          
          {/* Department-wise Leave Report */}
          <Button variant="outline" onClick={() => setShowAudit(true)} data-testid="audit-btn">
            <AlertTriangle className="h-4 w-4 mr-2" /> Audit
          </Button>
          <Button variant="outline" onClick={() => setShowDupeScanner(true)} data-testid="scan-leave-dupes-btn" className="border-rose-200 text-rose-700 hover:bg-rose-50">
            <AlertTriangle className="h-4 w-4 mr-2" /> Scan duplicates
          </Button>
          <Button variant="outline" onClick={() => setShowCleanup(true)} data-testid="data-cleanup-btn" className="border-amber-200 text-amber-700 hover:bg-amber-50">
            <AlertTriangle className="h-4 w-4 mr-2" /> Data Cleanup
            {brokenCount > 0 && (
              <Badge className="ml-2 bg-amber-500 hover:bg-amber-500 text-white" data-testid="broken-count-badge">{brokenCount}</Badge>
            )}
          </Button>
          <Button variant="outline" onClick={() => setShowRecoveryLog(true)} data-testid="recovery-log-btn" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
            <Users className="h-4 w-4 mr-2" /> Recovery Log
          </Button>
          <Popover open={deptReportOpen} onOpenChange={setDeptReportOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="dept-report-btn">
                <Users className="h-4 w-4 mr-2" /> Dept Report
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-sm mb-1">Department Leave Report</h4>
                  <p className="text-xs text-muted-foreground">Generate department-wise leave report with filters</p>
                </div>
                
                {/* Department Selection */}
                <div>
                  <Label className="text-xs font-medium">Department</Label>
                  <Select value={selectedDeptReport} onValueChange={setSelectedDeptReport}>
                    <SelectTrigger data-testid="dept-select-report" className="mt-1">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Year & Month Selection */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-medium">Year</Label>
                    <Select value={deptReportYear} onValueChange={setDeptReportYear}>
                      <SelectTrigger data-testid="dept-report-year" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Month</Label>
                    <Select value={deptReportMonth} onValueChange={setDeptReportMonth}>
                      <SelectTrigger data-testid="dept-report-month" className="mt-1">
                        <SelectValue placeholder="Full Year" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Full Year</SelectItem>
                        {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                          <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Leave Type Filter */}
                <div>
                  <Label className="text-xs font-medium">Leave Type (optional)</Label>
                  <Select value={deptReportLeaveType} onValueChange={setDeptReportLeaveType}>
                    <SelectTrigger data-testid="dept-report-leave-type" className="mt-1">
                      <SelectValue placeholder="All Leave Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Leave Types</SelectItem>
                      <SelectItem value="annual">Annual Leave</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                      <SelectItem value="emergency">Emergency Leave</SelectItem>
                      <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                      <SelectItem value="partial_unpaid">Partial Unpaid</SelectItem>
                      <SelectItem value="maternity">Maternity Leave</SelectItem>
                      <SelectItem value="paternity">Paternity Leave</SelectItem>
                      <SelectItem value="public_holiday">Public Holiday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Include Inactive Staff Toggle */}
                <div className="flex items-center space-x-2 py-1 border-t border-b">
                  <Checkbox 
                    id="deptReportInactive"
                    checked={deptReportIncludeInactive}
                    onCheckedChange={setDeptReportIncludeInactive}
                    data-testid="dept-report-inactive-toggle"
                  />
                  <Label 
                    htmlFor="deptReportInactive" 
                    className="text-xs cursor-pointer flex items-center gap-1"
                  >
                    <UserX className="h-3 w-3 text-gray-500" />
                    Include inactive staff (Resigned/Terminated/Suspended)
                  </Label>
                </div>
                
                {/* Download Buttons */}
                <div className="flex space-x-2">
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleDepartmentReport('pdf')}
                    disabled={!selectedDeptReport}
                    data-testid="dept-report-pdf-btn"
                  >
                    <Download className="h-3 w-3 mr-1" /> PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleDepartmentReport('excel')}
                    disabled={!selectedDeptReport}
                    data-testid="dept-report-excel-btn"
                  >
                    <Download className="h-3 w-3 mr-1" /> Excel
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          
          {!isViewer && (
            <>
              {/* Add PH Credit Dialog */}
              <Dialog open={phDialogOpen} onOpenChange={setPHDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" onClick={() => {
                    setPHFormData({ employee_id: selectedEmployee || '', earned_date: '', days: 1, reason: '' });
                    setPHDialogOpen(true);
                  }} data-testid="add-ph-credit-btn">
                    <Gift className="h-4 w-4 mr-2" /> Add PH Credit
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Public Holiday Credit</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPHCredit} className="space-y-4">
                    <div>
                      <Label>Employee</Label>
                      <Select 
                        value={phFormData.employee_id} 
                        onValueChange={(value) => setPHFormData(prev => ({ ...prev, employee_id: value }))}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>
                              <span className="flex items-center gap-2">
                                {emp.name}
                                {emp.employment_status && emp.employment_status !== 'Active' && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    emp.employment_status === 'Resigned' ? 'bg-yellow-100 text-yellow-700' :
                                    emp.employment_status === 'Terminated' ? 'bg-red-100 text-red-700' :
                                    emp.employment_status === 'Suspended' ? 'bg-orange-100 text-orange-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {emp.employment_status}
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date Earned (worked on off/PH day)</Label>
                      <Input 
                        type="date" 
                        required 
                        value={phFormData.earned_date}
                        onChange={(e) => setPHFormData(prev => ({ ...prev, earned_date: e.target.value }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">PH will expire 90 days from this date</p>
                    </div>
                    <div>
                      <Label>Days Earned</Label>
                      <Input 
                        type="number" 
                        min="0.5"
                        step="0.5"
                        value={phFormData.days}
                        onChange={(e) => setPHFormData(prev => ({ ...prev, days: parseFloat(e.target.value) || 1 }))}
                      />
                    </div>
                    <div>
                      <Label>Reason</Label>
                      <Input 
                        value={phFormData.reason}
                        onChange={(e) => setPHFormData(prev => ({ ...prev, reason: e.target.value }))}
                        placeholder="e.g., Worked on Christmas Day"
                      />
                    </div>
                    <div className="flex justify-end space-x-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setPHDialogOpen(false)}>Cancel</Button>
                      <Button type="submit">Add PH Credit</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Record Leave Dialog */}
              <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
                <DialogTrigger asChild>
                  <Button onClick={() => { 
                    setEditingLeave(null); 
                    setDocumentWarnings([]);
                    setFormExtraPaid(0);
                    setFormData({ employee_id: '', leave_type: 'annual', start_date: '', end_date: '', reason: '' }); 
                    setDialogOpen(true);
                  }} data-testid="record-leave-btn">
                    <Plus className="h-4 w-4 mr-2" /> Record Leave
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingLeave ? 'Edit Leave' : 'Record Leave'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>Employee</Label>
                      <Select 
                        value={formData.employee_id} 
                        onValueChange={(value) => handleFormChange('employee_id', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>
                              <span className="flex items-center gap-2">
                                {emp.name}
                                {emp.employment_status && emp.employment_status !== 'Active' && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    emp.employment_status === 'Resigned' ? 'bg-yellow-100 text-yellow-700' :
                                    emp.employment_status === 'Terminated' ? 'bg-red-100 text-red-700' :
                                    emp.employment_status === 'Suspended' ? 'bg-orange-100 text-orange-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {emp.employment_status}
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {formExtraPaid > 0 && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="extra-paid-reminder">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-800">
                          <b>Reminder:</b> this employee was paid <b>{formExtraPaid}</b> extra day(s) in advance.
                          Their balance is already reduced by this amount — adjust this leave's paid/unpaid split if those advance days should be worked off here.
                        </p>
                      </div>
                    )}
                    <div>
                      <Label>Leave Type</Label>
                      <Select 
                        value={formData.leave_type} 
                        onValueChange={(value) => handleFormChange('leave_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAVE_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Approved by (Manager)</Label>
                      <Select
                        value={formData.approved_by_manager_id || ''}
                        onValueChange={(value) => {
                          const m = managers.find((x) => x.id === value);
                          setFormData((prev) => ({ ...prev, approved_by_manager_id: value, approved_by_manager: m ? m.name : '' }));
                        }}
                      >
                        <SelectTrigger data-testid="approved-by-manager-select">
                          <SelectValue placeholder="Select approving manager (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {managers.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name}{m.position ? ` · ${m.position}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Multi-range toggle (only when creating, not editing) */}
                    {!editingLeave && (
                      <div className="flex items-center gap-2 p-3 rounded-md bg-violet-50 border border-violet-200">
                        <Checkbox
                          id="multi-mode"
                          checked={multiMode}
                          onCheckedChange={(checked) => {
                            setMultiMode(!!checked);
                            if (checked && formData.start_date && formData.end_date) {
                              setRanges([{ start_date: formData.start_date, end_date: formData.end_date }]);
                            }
                          }}
                          data-testid="multi-range-checkbox"
                        />
                        <Label htmlFor="multi-mode" className="text-sm cursor-pointer font-medium">
                          Employee took leave on multiple non-consecutive dates
                        </Label>
                      </div>
                    )}

                    {!multiMode ? (
                      <>
                        <div>
                          <Label>Start Date</Label>
                          <Input 
                            type="date" 
                            required 
                            value={formData.start_date}
                            onChange={(e) => handleFormChange('start_date', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>End Date</Label>
                          <Input 
                            type="date" 
                            required 
                            value={formData.end_date}
                            onChange={(e) => handleFormChange('end_date', e.target.value)}
                          />
                        </div>

                        {/* Live paid/unpaid (LOP) balance-impact preview */}
                        {(splitLoading || splitPreview) && (
                          <div
                            className={`rounded-md border p-3 text-sm ${
                              splitPreview?.counts_as_lop === false && splitPreview?.leave_type === 'mgmt_unpaid'
                                ? 'bg-amber-50 border-amber-200'
                                : splitPreview?.exceeds_balance
                                ? 'bg-rose-50 border-rose-200'
                                : splitPreview?.applicable === false
                                ? 'bg-slate-50 border-slate-200'
                                : 'bg-emerald-50 border-emerald-200'
                            }`}
                            data-testid="leave-split-preview"
                          >
                            {splitLoading ? (
                              <span className="text-gray-500">Calculating balance impact…</span>
                            ) : splitPreview?.applicable === false ? (
                              <span className="text-slate-600">{splitPreview.note}</span>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-700">Balance impact</span>
                                  <span className="text-xs text-gray-500">
                                    {splitPreview.total_days} day{splitPreview.total_days === 1 ? '' : 's'}
                                    {splitPreview.total_available != null &&
                                      ` · ${splitPreview.total_available} available`}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                                    <span className="font-semibold text-emerald-700" data-testid="split-paid-days">
                                      {splitPreview.paid_days}
                                    </span>
                                    <span className="text-gray-600">paid</span>
                                  </span>
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className={`h-2.5 w-2.5 rounded-full ${splitPreview.leave_type === 'mgmt_unpaid' ? 'bg-amber-500' : 'bg-rose-500'}`} />
                                    <span className={`font-semibold ${splitPreview.leave_type === 'mgmt_unpaid' ? 'text-amber-700' : 'text-rose-700'}`} data-testid="split-unpaid-days">
                                      {splitPreview.unpaid_days}
                                    </span>
                                    <span className="text-gray-600">
                                      {splitPreview.leave_type === 'mgmt_unpaid' ? 'unpaid (salary only · not LOP)' : 'unpaid (LOP)'}
                                    </span>
                                  </span>
                                </div>
                                {(splitPreview.exceeds_balance || splitPreview.leave_type === 'mgmt_unpaid') && (
                                  <p className={`text-xs flex items-center gap-1 pt-0.5 ${splitPreview.leave_type === 'mgmt_unpaid' ? 'text-amber-700' : 'text-rose-600'}`}>
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    {splitPreview.note ||
                                      `Exceeds balance — ${splitPreview.unpaid_days} day(s) will be unpaid.`}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="space-y-2" data-testid="multi-range-list">
                        <HrSeparateDates leaveType={formData.leave_type} onAdd={(runs) => setRanges(rs => [...rs.filter(r => r.start_date && r.end_date), ...runs.map(run => ({ start_date: run[0], end_date: run[run.length - 1] })).filter(n => !rs.some(r => r.start_date === n.start_date && r.end_date === n.end_date))])} />
                        <Label>Date Ranges</Label>
                        {ranges.map((r, idx) => (
                          <div key={idx} className="flex items-end gap-2 p-2 rounded-md bg-gray-50 border" data-testid={`range-row-${idx}`}>
                            <div className="flex-1">
                              <Label className="text-[11px] text-gray-500">From</Label>
                              <Input
                                type="date"
                                value={r.start_date}
                                onChange={(e) => setRanges(rs => rs.map((x, i) => i === idx ? { ...x, start_date: e.target.value } : x))}
                                required
                                data-testid={`range-start-${idx}`}
                              />
                            </div>
                            <div className="flex-1">
                              <Label className="text-[11px] text-gray-500">To</Label>
                              <Input
                                type="date"
                                value={r.end_date}
                                onChange={(e) => setRanges(rs => rs.map((x, i) => i === idx ? { ...x, end_date: e.target.value } : x))}
                                required
                                data-testid={`range-end-${idx}`}
                              />
                            </div>
                            {ranges.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setRanges(rs => rs.filter((_, i) => i !== idx))}
                                className="text-red-600 mb-0.5"
                                data-testid={`range-remove-${idx}`}
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setRanges(rs => [...rs, { start_date: '', end_date: '' }])}
                          className="w-full border-dashed"
                          data-testid="add-range-btn"
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add another date range
                        </Button>
                        <p className="text-[11px] text-gray-500">
                          Each range will be saved as a separate leave record for this employee.
                        </p>
                      </div>
                    )}
                    
                    {/* Document Expiry Warning */}
                    {documentWarnings.length > 0 && (
                      <Alert variant="destructive" className="bg-orange-50 border-orange-300" data-testid="document-clash-warning">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <AlertTitle className="text-orange-800">Document Renewal Warning</AlertTitle>
                        <AlertDescription className="text-orange-700">
                          <p className="mb-2">The following documents expire within 30 days of the leave period:</p>
                          <ul className="list-disc list-inside space-y-1">
                            {documentWarnings.map((warning, idx) => (
                              <li key={idx}>
                                <strong>{warning.document_type}</strong>: {warning.document_number} expires on {new Date(warning.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ({warning.days_until_expiry} days)
                              </li>
                            ))}
                          </ul>
                          <p className="mt-2 text-sm">Consider scheduling leave after document renewal.</p>
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    <div>
                      <Label>Reason</Label>
                      <Input 
                        value={formData.reason}
                        onChange={(e) => handleFormChange('reason', e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    
                    {/* Flight Ticket Checkbox - only show for annual leave */}
                    {formData.leave_type === 'annual' && (
                      <div className="flex items-center space-x-2 pt-2 pb-2 border-t border-b">
                        <Checkbox 
                          id="flight_ticket"
                          checked={formData.flight_ticket_provided}
                          onCheckedChange={(checked) => handleFormChange('flight_ticket_provided', checked)}
                          data-testid="flight-ticket-checkbox"
                        />
                        <Label 
                          htmlFor="flight_ticket" 
                          className="text-sm cursor-pointer font-medium"
                        >
                          Flight ticket provided with this leave
                        </Label>
                      </div>
                    )}

                    {/* Email-based approval: route this leave to a manager */}
                    {!editingLeave && !multiMode && (
                      <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid="approval-section">
                        <Label className="text-sm font-medium flex items-center gap-1.5 text-indigo-800">
                          <Send className="h-3.5 w-3.5" /> Send for manager approval (optional)
                        </Label>
                        <Input
                          type="email"
                          value={approverEmail}
                          onChange={(e) => setApproverEmail(e.target.value)}
                          placeholder="approver@company.com"
                          data-testid="approver-email-input"
                        />
                        <p className="text-[11px] text-indigo-600/80">
                          Instead of recording directly, email this manager a secure Approve/Reject link. The leave is created only after they approve.
                        </p>
                      </div>
                    )}

                    <div className="flex justify-end space-x-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>Cancel</Button>
                      {!editingLeave && !multiMode && (
                        <Button
                          type="button"
                          variant="outline"
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          onClick={handleSendForApproval}
                          disabled={sendingApproval}
                          data-testid="send-for-approval-btn"
                        >
                          <Send className="h-4 w-4 mr-1.5" /> {sendingApproval ? 'Sending…' : 'Send for Approval'}
                        </Button>
                      )}
                      <Button type="submit" data-testid="save-leave-btn">{editingLeave ? 'Update Leave' : 'Save Leave'}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Year + Month Shortcuts */}
      <div className="flex flex-wrap items-center gap-2" data-testid="year-month-filters">
        <div className="flex items-center gap-1.5 mr-2">
          <span className="text-xs font-semibold text-gray-600">Year:</span>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(v === 'all' ? 'all' : parseInt(v, 10))}>
            <SelectTrigger
              className="h-8 w-[110px] rounded-full border-orange-200 bg-orange-50 text-orange-700 font-semibold text-xs focus:ring-orange-300"
              data-testid="year-filter-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 3 + i).map(y => (
                <SelectItem key={y} value={String(y)} data-testid={`year-option-${y}`}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-1.5" data-testid="month-shortcuts">
          <button
            onClick={() => setSelectedMonth(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${selectedMonth === null ? 'bg-orange-500 text-white shadow-md' : 'bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200'}`}
            data-testid="month-all"
          >
            All
          </button>
          {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
            <button
              key={m}
              onClick={() => setSelectedMonth(i + 1)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${selectedMonth === i + 1 ? 'bg-orange-500 text-white shadow-md' : 'bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200'}`}
              data-testid={`month-${m.toLowerCase()}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Pending Leave Approvals */}
      {pendingLeaves.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50/50" data-testid="pending-leaves-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600" />
              Pending Leave Approvals ({pendingLeaves.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingLeaves.map(leave => {
                const emp = employees.find(e => e.id === leave.employee_id) || allEmployees.find(e => e.id === leave.employee_id);
                return (
                  <div key={leave.id} className="flex items-center justify-between bg-white rounded-lg p-3 border" data-testid={`pending-leave-${leave.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{emp?.name || leave.employee_name || 'Unknown'}</span>
                        <Badge variant="outline" className="text-[10px]">{leave.leave_type}</Badge>
                        <WaitingChip leave={leave} />
                      </div>
                      {leave.pending_dates ? (
                        <div className="mt-1" data-testid={`pending-multi-dates-${leave.id}`}>
                          <p className="text-[11px] text-sky-700 font-medium">Separate dates · {leave.pending_dates.length} day(s) — click a range or day to reject only that part</p>
                          <div className="mt-1">
                            <GroupDatesSelector dates={leave.pending_dates} rejected={hrRejectDates[leave.id] || []}
                              onChange={(list) => setHrRejectDates(m => ({ ...m, [leave.id]: list }))} editable testPrefix={`hr-date-${leave.id}`} />
                          </div>
                        </div>
                      ) : (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {leave.start_date} to {leave.end_date} ({leave.days} day{leave.days !== 1 ? 's' : ''})
                      </p>
                      )}
                      {leave.reason && <p className="text-xs text-gray-400 mt-0.5 truncate">{leave.reason}</p>}
                      <ApprovalRecipientNote leave={leave} employee={emp} allEmployees={allEmployees} />
                      {overdrawMap[leave.id] && (
                        <p className="text-xs text-red-600 font-medium mt-1 flex items-center gap-1" data-testid={`overdraw-warning-${leave.id}`}>
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Approving overdraws annual balance to {overdrawMap[leave.id].projected_balance} days ({overdrawMap[leave.id].overdraw_days} LOP)
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 ml-3">
                      <ResendApprovalMenu
                        leave={leave}
                        managers={(emp?.reporting_manager_ids || []).map(id => allEmployees.find(e => e.id === id)).filter(Boolean)}
                        onDone={loadData}
                      />
                      <Button
                        size="sm"
                        variant="default"
                        className="h-7 text-xs bg-green-600 hover:bg-green-700"
                        onClick={() => handleLeaveAction(leave.id, 'approve')}
                        data-testid={`approve-leave-${leave.id}`}
                      >
                        {leave.pending_dates && (hrRejectDates[leave.id] || []).length ? `Approve ${leave.pending_dates.length - hrRejectDates[leave.id].length}` : 'Approve'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 text-xs"
                        onClick={() => handleLeaveAction(leave.id, 'reject')}
                        data-testid={`reject-leave-${leave.id}`}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Employee Selection & Balance */}
        <Card>
          <CardHeader>
            <CardTitle>Select Employee</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Toggle for showing old employees */}
            <div className="flex items-center space-x-2 pb-2 border-b">
              <Checkbox 
                id="showOldEmployees"
                checked={showOldEmployees}
                onCheckedChange={setShowOldEmployees}
                data-testid="show-old-employees-checkbox"
              />
              <Label 
                htmlFor="showOldEmployees" 
                className="text-sm cursor-pointer flex items-center gap-2"
              >
                <UserX className="h-4 w-4 text-gray-500" />
                Include old/inactive employees
              </Label>
            </div>
            
            <Select value={selectedEmployee} onValueChange={handleEmployeeSelect}>
              <SelectTrigger>
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    <span className="flex items-center gap-2">
                      {emp.name}
                      {emp.employment_status && emp.employment_status !== 'Active' && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          emp.employment_status === 'Resigned' ? 'bg-yellow-100 text-yellow-700' :
                          emp.employment_status === 'Terminated' ? 'bg-red-100 text-red-700' :
                          emp.employment_status === 'Suspended' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {emp.employment_status}
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Leave Salary Download Section */}
            {selectedEmployee && selectedEmployee !== 'all' && (
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-orange-800">Leave Salary</span>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-blue-300 text-blue-700 hover:bg-blue-100" 
                      data-testid="leave-salary-view-btn"
                      onClick={() => setLeaveSalaryPreviewOpen(true)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Dialog open={leaveSalaryDialogOpen} onOpenChange={setLeaveSalaryDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-100" data-testid="leave-salary-btn">
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Download Leave Salary</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="text-sm text-gray-600">
                            Employee: <span className="font-medium">{employees.find(e => e.id === selectedEmployee)?.name}</span>
                          </div>
                          
                          <div>
                            <Label>Leave Type</Label>
                            <Select value={leaveSalaryType} onValueChange={setLeaveSalaryType}>
                              <SelectTrigger data-testid="leave-salary-type-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="annual">Annual Leave</SelectItem>
                                <SelectItem value="maternity">Maternity Leave</SelectItem>
                                <SelectItem value="paternity">Paternity Leave</SelectItem>
                                <SelectItem value="sick">Sick Leave</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Leave Start Date *</Label>
                              <Input 
                                type="date" 
                                value={leaveStartDate} 
                                onChange={(e) => setLeaveStartDate(e.target.value)}
                                className="mt-1"
                                required
                              />
                            </div>
                            <div>
                              <Label>Leave End Date *</Label>
                              <Input 
                                type="date" 
                                value={leaveEndDate} 
                                onChange={(e) => setLeaveEndDate(e.target.value)}
                                className="mt-1"
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setLeaveSalaryDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button 
                              variant="outline" 
                              className="border-green-500 text-green-700 hover:bg-green-50"
                              onClick={() => handleDownloadLeaveSalary('excel')}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Excel
                            </Button>
                            <Button 
                              className="bg-orange-500 hover:bg-orange-600"
                              onClick={() => handleDownloadLeaveSalary('pdf')}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              PDF
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <p className="text-xs text-orange-600">
                  Download leave salary calculation for the selected period
                </p>
              </div>
            )}

            <LeaveBalanceSection leaveBalance={leaveBalance} />

            {selectedEmployee && selectedEmployee !== 'all' && (
              <HistoricalLeaveCard
                employeeId={selectedEmployee}
                onSaved={() => loadBalance(selectedEmployee)}
              />
            )}

            {selectedEmployee && selectedEmployee !== 'all' && (
              <div className="flex items-center justify-between gap-2 mt-3 p-3 bg-sky-50 border border-sky-200 rounded-lg">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-sky-800">Leave Ledger</p>
                  <p className="text-[11px] text-sky-600">Running statement of leaves & balance</p>
                </div>
                <Button size="sm" variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-100 shrink-0"
                  onClick={() => setShowLedger(true)} data-testid="open-ledger-btn">
                  <FileText className="h-4 w-4 mr-1" /> View Ledger
                </Button>
              </div>
            )}

            {/* Portal visibility toggle (enable these views on the Employee Portal later) */}
            {!isViewer && (
              <div className="flex items-center justify-between gap-2 mt-3 p-3 bg-gray-50 border rounded-lg" data-testid="portal-transparency-toggle">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-700">Show on Employee Portal</p>
                  <p className="text-[11px] text-gray-500">Let staff see their own Used Breakdown & Ledger</p>
                </div>
                <button
                  onClick={togglePortalTransparency}
                  data-testid="portal-transparency-btn"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${portalTransparency ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${portalTransparency ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            )}
            
            {selectedEmployee && !isViewer && (
              <AdvancePaymentSection 
                employeeId={selectedEmployee}
                onPaymentChange={loadData}
              />
            )}
            
            <PHCreditsSection 
              phCredits={phCredits}
              phBalance={phBalance}
              selectedEmployee={selectedEmployee}
              onDeletePHCredit={handleDeletePHCredit}
              onSetPHExpiry={handleSetPHExpiry}
              isViewer={isViewer}
            />
          </CardContent>
        </Card>

        {/* Leave Records */}
        <div className="lg:col-span-2">
          {/* Leave type filter chips */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="leave-type-filter">
            <span className="text-xs font-medium text-gray-500 mr-1">Type:</span>
            <button
              onClick={() => setSelectedLeaveType('all')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedLeaveType === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="leave-type-chip-all"
            >
              All <span className="opacity-70">({dateScopedLeaves.length})</span>
            </button>
            {RECORD_TYPE_FILTERS.filter(t => typeCounts[t.value]).map(t => (
              <button
                key={t.value}
                onClick={() => setSelectedLeaveType(t.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedLeaveType === t.value ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`leave-type-chip-${t.value}`}
              >
                {t.label} <span className="opacity-70">({typeCounts[t.value]})</span>
              </button>
            ))}
          </div>
          <LeaveRecordsList 
            filteredLeaves={filteredLeaves}
            employees={allEmployees && allEmployees.length ? allEmployees : employees}
            onEdit={handleEdit}
            onDelete={handleDeleteLeave}
            onDownloadLeaveSalary={handleDownloadLeaveRecordSalary}
            isViewer={isViewer}
            onDataRefresh={loadData}
          />
        </div>
      </div>

      {/* Leave Salary Preview Dialog */}
      <LeaveSalaryPreviewDialog
        open={leaveSalaryPreviewOpen}
        onClose={() => setLeaveSalaryPreviewOpen(false)}
        employee={employees.find(e => e.id === selectedEmployee)}
        employees={employees}
      />

      {/* Delete Leave Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Leave Record"
        description="Are you sure you want to delete this leave record? This will affect the employee's leave balance."
        itemName={leaveToDelete ? `${leaveToDelete.employee_name}'s ${leaveToDelete.leave_type} leave (${leaveToDelete.days} days)` : ''}
        onConfirm={confirmDeleteLeave}
        loading={deleting}
      />

      {showAudit && <LeaveAuditDialog onClose={() => setShowAudit(false)} />}
      <LeaveLedgerDialog
        open={showLedger}
        onOpenChange={setShowLedger}
        employeeId={selectedEmployee}
        year={selectedYear}
      />
      <BrokenLeavesDialog
        open={showCleanup}
        onOpenChange={setShowCleanup}
        onChanged={() => { loadData(); loadBrokenCount(); }}
      />
      <RecoveryLogDialog open={showRecoveryLog} onOpenChange={setShowRecoveryLog} />
      <DuplicateScannerDialog open={showDupeScanner} onOpenChange={setShowDupeScanner} scope="leaves" />
    </div>
  );
};

export default Leaves;
